import {
  createFetch as createZapierFetch,
  FetchInit,
  FetchOptions,
  responseBody,
} from '@zapier/transfer-fetch';

import getSession from './methods/getSession';
import logout from './methods/logout';
import getMetadata from './methods/getMetadata';
import getAuthentications from './methods/getAuthentications';
import testAuthentication from './methods/testAuthentication';
import getNeeds from './methods/getNeeds';
import getPrefillChoices from './methods/getPrefillChoices';
import getGives from './methods/getGives';
import getSamples from './methods/getSamples';
import createZap from './methods/createZap';
import createWorkflowZapFromTransfer from './methods/createWorkflowZapFromTransfer';
import getZap from './methods/getZap';
import updateZap from './methods/updateZap';
import publishZap from './methods/publishZap';
import getAccounts from './methods/getAccounts';
import runEnablementChecks from './methods/runEnablementChecks';
import getPublicApps from './methods/getPublicApps';
import getPrivateApps from './methods/getPrivateApps';

import throwForStatus from '../../middleware/throwForStatus';
import addParams from '../shared/utils/addParams';

import createErrorHandler from '../shared/utils/createErrorHandler';

import { Params } from '../shared/types';
import getImplementations from './methods/getImplementations';
import getEditAccess from './methods/getEditAccess';

/** Return an object that exposes methods for calling various Zapier endpoints. */
export const createClient = (options?: FetchOptions) => {
  const zapierFetch = createZapierFetch({
    // We cannot use defaultAfterResponse as that includes authenticationError
    // and the convert-to-nodes endpoints return 401 on errors
    // that we'd like to parse in detail with our own throwForStatus.
    afterResponse: [responseBody, throwForStatus],
    ...options,
  });

  const fetch = async <TBody = unknown>(
    url: string,
    init?: FetchInit & {
      params?: Params;
    }
  ) => {
    const { params, ...passedInit } = init || {};
    return (
      // TODO: Convert `addParams` to middleware after https://github.com/zapier/foundations-frontend/issues/42
      zapierFetch<TBody>(addParams(url, params), passedInit)
        .catch(createErrorHandler(url))
        .then(({ body }) => body)
    );
  };

  return {
    fetch,
    /** Fetches the session of the currently logged-in user. */
    getSession: getSession.bind(null, fetch),
    /** Log the user out. */
    logout: logout.bind(null, fetch),
    /** Fetches data about the apps and actions used in the zap. */
    getMetadata: getMetadata.bind(null, fetch),
    /** Fetches the user's authenticated connections for a given app. */
    getAuthentications: getAuthentications.bind(null, fetch),
    /** Succeeds if an authentication is able to connect to an app. */
    testAuthentication: testAuthentication.bind(null, fetch),
    /** Fetches the edit access for a zap. */
    getEditAccess: getEditAccess.bind(null, fetch),
    /** Fetches the needs for an action. */
    getNeeds: getNeeds.bind(null, fetch),
    /** Fetches the choices for a `prefill` need. */
    getPrefillChoices: getPrefillChoices.bind(null, fetch),
    /** Fetches the fields returned by an action. */
    getGives: getGives.bind(null, fetch),
    /** Tests a zap step and returns the generated samples. */
    getSamples: getSamples.bind(null, fetch),
    /** Creates a new ZDL-based zap. */
    createZap: createZap.bind(null, fetch),
    /** Given a transfer zap, creates a new workflow zap with identical ZDL. */
    createWorkflowZapFromTransfer: createWorkflowZapFromTransfer.bind(
      null,
      fetch
    ),
    /** Updates the ZDL of a zap. */
    updateZap: updateZap.bind(null, fetch),
    /** Fetches a user's zap. */
    getZap: getZap.bind(null, fetch),
    /** Coverts the draft ZDL into a published version of the zap. */
    publishZap: publishZap.bind(null, fetch),
    getImplementations: getImplementations.bind(null, fetch),
    getAccounts: getAccounts.bind(null, fetch),
    runEnablementChecks: runEnablementChecks.bind(null, fetch),
    getPublicApps: getPublicApps.bind(null, fetch),
    getPrivateApps: getPrivateApps.bind(null, fetch),
  };
};

export default createClient;
